<template>
  <div
    id="app"
    :class="appClasses"
    @click="closeDropdowns">
    <notification />
    <topbar v-if="!isGuest"></topbar>

    <div
      v-if="!isGuest"
      class="app-content">
      <sidebar />
      <notifications />

      <div class="app-view">
        <transition name="switch">
          <router-view :key="$route.fullPath" />
        </transition>
      </div>

      <prompt />
      <alert />
      <confirm />
      <search-popup />
      <reservation-details />
      <survey-results-popup />
      <customer-popup />
      <customer-form-popup />
      <customer-note-popup />
      <change-date-popup />
      <invoice-details-popup />
      <payment-status-popup />
      <search-date-form :key="searchDateFormUniqueID" />
      <block-date-popup v-if="configurationLoaded" />
    </div>

    <transition name="switch">
      <router-view v-if="isGuest" />
    </transition>

    <a
      v-if="isGuest"
      href="#cookie-settings"
      @click.prevent="openCookieBanner()">
      {{ $t('topBar.cookieSettings') }}
    </a>
  </div>
</template>

<script>
import Alert from '../ui/Alert';
import BlockDatePopup from '../ui/BlockDatePopup';
import ChangeDatePopup from './../ui/ChangeDatePopup';
import Confirm from '../ui/Confirm';
import CustomerPopup from './../ui/CustomerPopup';
import CustomerFormPopup from './../ui/CustomerFormPopup';
import CustomerNotePopup from './../ui/CustomerNotePopup';
import ErrorUtils from './../helpers/ErrorUtils.js';
import InvoiceDetailsPopup from '../ui/InvoiceDetailsPopup.vue';
import NotificationBar from '../ui/Notification';
import Notifications from '../ui/Notifications';
import PaymentStatusPopup from '../ui/PaymentStatusPopup';
import Prompt from '../ui/Prompt';
import ReservationDetails from '../ui/ReservationDetails';
import SanitizeUtils from '../helpers/SanitizeUtils';
import SearchPopup from '../ui/SearchPopup';
import SearchDateForm from '../ui/SearchDateForm/template';
import Sidebar from '../ui/Sidebar';
import SurveyResultsPopup from '../ui/SurveyResultsPopup';
import TopBar from '../ui/TopBar';
import UserTypes from './../mixins/UserTypes';

export default {
  name: 'App',
  mixins: [
    UserTypes
  ],
  components: {
    'alert': Alert,
    'block-date-popup': BlockDatePopup,
    'change-date-popup': ChangeDatePopup,
    'confirm': Confirm,
    'customer-popup': CustomerPopup,
    'customer-form-popup': CustomerFormPopup,
    'customer-note-popup': CustomerNotePopup,
    'invoice-details-popup': InvoiceDetailsPopup,
    'notification': NotificationBar,
    'notifications': Notifications,
    'payment-status-popup': PaymentStatusPopup,
    'prompt': Prompt,
    'reservation-details': ReservationDetails,
    'search-popup': SearchPopup,
    'search-date-form': SearchDateForm,
    'sidebar': Sidebar,
    'survey-results-popup': SurveyResultsPopup,
    'topbar': TopBar
  },
  data () {
    return {
      configurationLoaded: false,
      interval: null,
      notificationsInterval: null,
      keepAliveTimeout: null,
      searchDateFormUniqueID: new Date().getTime(),
      waitingInquiriesLoaded: false,
      notifications: false,
      notificationsLoaded: false,
      notificationsReloadInterval: 15000,
      waitingInquiriesReloadInterval: 15000
    };
  },
  computed: {
    appClasses () {
      return {
        'app': true,
        'dark-bg': this.isGuest
      };
    },
    isGuest () {
      let guestRoutes = [
        'AutoLogin',
        'Login',
        'LoginAfterPasswordReset',
        'PasswordReminder',
        'PasswordReset',
        'PasswordResetWithoutToken',
        'Register',
        'RegisterWithPackage',
        null
      ];
      return guestRoutes.indexOf(this.$route.name) > -1;
    }
  },
  mounted () {
    document.body.setAttribute('data-lang', this.$config.uiLanguage);
    this.$bus.$on('configuration-loaded', this.markConfigurationAsLoaded);

    if (document.body.getAttribute('data-inquiries') && parseInt(document.body.getAttribute('data-inquiries'), 10) > 10000) {
      this.waitingInquiriesReloadInterval = parseInt(document.body.getAttribute('data-inquiries'), 10);
    }

    if (document.body.getAttribute('data-notifications') && parseInt(document.body.getAttribute('data-notifications'), 10) > 10000) {
      this.notificationsReloadInterval = parseInt(document.body.getAttribute('data-notifications'), 10);
    }

    setTimeout(() => {
      if (SanitizeUtils.getToken()) {
        clearTimeout(this.interval);

        if (this.userCanAddReservation) {
          this.loadWaitingQueries();
        }

        this.loadNotifications();
        this.loadBookeroNews();
      }
    }, 1000);

    this.$bus.$on('login-success', this.loadWaitingQueriesWithDelay);
    this.$bus.$on('login-success', this.loadNotificationsWithDelay);
    this.$bus.$on('login-success', this.loadBookeroNews);
    this.$bus.$on('login-success', this.configureBugSnagUser);
    this.$bus.$on('login-success', this.configureGaUser);
    this.$bus.$on('update-favicon', this.updateFavicon);
    this.$bus.$on('force-update-waiting-queries', this.loadWaitingQueries);
    this.keepAlive();

    setTimeout(() => {
      this.resizeContent();
      window.addEventListener('resize', this.resizeContent);
    }, 0);

    let self = this;

    window.bookero = {
      http: self.$http,
      updateFaviconCounter () {
        if (!self.$store.state || !self.$store.state.global) {
          return;
        }

        let notificationsCount = self.$store.state.global.waitingInquiries.length;
        notificationsCount += self.$store.state.global.notifications.filter(n => !n.readed_at).length;

        if (notificationsCount) {
          self.$favicon.badge(notificationsCount);
        } else {
          self.$favicon.reset();
        }
      },
      permissionsAvailable () {
        return self.$store.state && self.$store.state.config && self.$store.state.config.permissions;
      },
      hasPermissions (permissionName) {
        return self.$store.state.config.permissions && self.$store.state.config.permissions[permissionName];
      },
      userHasId () {
        return self.$store.state.config.user.id;
      },
      userIsWorker () {
        return self.$store.state.config.user.parent_id > 0 && self.$store.state.config.user.worker.worker_type === 0;
      },
      userIsReceptionist () {
        return self.$store.state.config.user.parent_id > 0 && self.$store.state.config.user.worker && self.$store.state.config.user.worker.worker_type === 1;
      },
      userHasParent () {
        return self.$store.state.config.user.parent_id > 0;
      },
      getPackageExpirationData () {
        if (self.$store.state.config.company && self.$store.state.config.company.package_valid_to) {
          return {
            valid_to: self.$store.state.config.company.package_valid_to,
            expires_days: self.$store.state.config.expires_days
          };
        }

        return false;
      },
      showExpirationMessage () {
        self.$bus.$emit('alert-display', {
          title: self.$t('app.systemExpired.title'),
          desc: self.$t('app.systemExpired.desc'),
          okLabel: self.$t('common.ui.ok'),
          okClick: () => true,
          isDanger: true
        });
      },
      translations: {
        $t (phrase) {
          return self.$t(phrase);
        }
      },
      showLoadingChunkAlert (error) {
        ErrorUtils.reportError('chunk', false, error.message);

        self.$bus.$emit('confirm-display', {
          title: self.$t('app.loadingChunkAlert.title'),
          desc: self.$t('app.loadingChunkAlert.desc'),
          okLabel: self.$t('app.loadingChunkAlert.btn'),
          okClick: () => {
            window.location.reload()
          },
          isDanger: true
        });
      },
      showReservationDetails (id) {
        self.$bus.$emit('show-reservation-details-popup', id, 'reservation');
      }
    };
  },
  methods: {
    closeDropdowns () {
      this.$bus.$emit('body-clicked');
    },
    loadWaitingQueriesWithDelay () {
      setTimeout(() => this.loadWaitingQueries(), 1000);
    },
    loadNotificationsWithDelay () {
      setTimeout(() => this.loadNotifications(), 1000);
    },
    loadWaitingQueries () {
      if (!this.$store.state.config.user.id) {
        clearTimeout(this.interval);
        this.interval = setTimeout(() => this.loadWaitingQueries(), this.waitingInquiriesReloadInterval);
        return;
      }

      this.$http.get('/panel/dashboard/waiting-queries').then(response => {
        this.waitingInquiriesLoaded = true;
        this.$store.commit('global/setWaitingInquiries', response.data.inquiries);

        if (response.data.inquiries.length && this.getFaviconBadgeValue()) {
          this.$favicon.badge(this.getFaviconBadgeValue());
        } else {
          this.$favicon.reset();
        }

        clearTimeout(this.interval);
        this.interval = setTimeout(() => this.loadWaitingQueries(), this.waitingInquiriesReloadInterval);
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/dashboard/waiting-queries', this.$router);
        clearTimeout(this.interval);
        this.interval = setTimeout(() => this.loadWaitingQueries(), this.waitingInquiriesReloadInterval);
      });
    },
    loadNotifications () {
      if (!this.$config.notificationsEnabled) {
        return;
      }

      if (!this.$store.state.config.user.id) {
        clearTimeout(this.notificationsInterval);
        this.notificationsInterval = setTimeout(() => this.loadNotifications(), this.notificationsReloadInterval);
        return;
      }

      this.$http.get('/panel/notifications/get-notifications').then(response => {
        this.notificationsLoaded = true;

        if (response.data) {
          if (response.data.app_version) {
            this.checkVersion(response.data.app_version);
          }

          if (response.data.data) {
            this.$store.commit('global/setNotifications', response.data.data);
            if (response.data.data.length && this.getFaviconBadgeValue()) {
              this.$favicon.badge(this.getFaviconBadgeValue());
            } else {
              this.$favicon.reset();
            }
          }
        }

        clearTimeout(this.notificationsInterval);
        this.notificationsInterval = setTimeout(() => {
          this.loadNotifications();
        }, this.notificationsReloadInterval);
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/notifications/get-notifications', this.$router);
        clearTimeout(this.notificationsInterval);
        this.notificationsInterval = setTimeout(() => {
          this.loadNotifications();
        }, this.notificationsReloadInterval);
      });
    },
    loadBookeroNews () {
      this.$http.post('/panel/news/get').then(response => {
        this.$store.commit('global/setBookeroNews', JSON.parse(JSON.stringify(response.data)));
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/news/get', this.$router, this.$bus);
      });
    },
    getFaviconBadgeValue () {
      if (!this.userCanAddReservation) {
        return this.$store.state.global.notifications.filter(n => !n.readed_at).length;
      }

      return this.$store.state.global.waitingInquiries.length + this.$store.state.global.notifications.filter(n => !n.readed_at).length;
    },
    keepAlive () {
      this.keepAliveTimeout = setTimeout(() => {
        localStorage.setItem('bookero-last-visit-time', new Date().getTime());
        this.keepAlive();
      }, 5 * 60 * 1000);
    },
    resizeContent () {
      let currentHeight = window.innerHeight;
      let currentWidth = window.innerWidth;

      if (!document.querySelector('.app-view')) {
        return;
      }

      if (currentWidth > 640) {
        document.querySelector('.app-view').style.minHeight = 'calc(' + currentHeight + 'px - 7rem)';
      } else {
        document.querySelector('.app-view').style.minHeight = currentHeight + 'px';
      }
    },
    updateFavicon () {
      let notificationsCount = this.getFaviconBadgeValue();

      if (notificationsCount) {
        this.$favicon.badge(notificationsCount);
      } else {
        this.$favicon.reset();
      }
    },
    configureBugSnagUser () {
      if (typeof window.bugsnagClient !== 'undefined') {
        window.bugsnagClient.user = {
          id: this.$store.state.config.user.id
        };
      }
    },
    configureGaUser () {
      if (typeof window.ga !== 'undefined' && typeof this.$store.state.config.user.id === 'number') {
        window.ga('set', 'dimension1', this.$store.state.config.user.id);
      } else if (typeof window.ga !== 'undefined') {
        window.ga('set', 'dimension1', 0);
      }
    },
    markConfigurationAsLoaded () {
      this.configurationLoaded = true;
    },
    checkVersion (version) {
      if (typeof version === 'string') {
        let currentAppVersion = parseFloat(this.$config.appVersion);
        let receivedAppVersion = parseFloat(version);

        if (receivedAppVersion > currentAppVersion) {
          this.$bus.$emit('bookero-new-version-available');
        }
      }
    },
    openCookieBanner () {
      let cnEvent = new CustomEvent('cn-notice-display');
      document.dispatchEvent(cnEvent);
    }
  },
  beforeDestroy () {
    clearTimeout(this.interval);
    clearTimeout(this.keepAliveTimeout);
    this.$bus.$off('configuration-loaded', this.markConfigurationAsLoaded);
    this.$bus.$off('login-success', this.loadWaitingQueriesWithDelay);
    this.$bus.$off('login-success', this.loadNotificationsWithDelay);
    this.$bus.$off('login-success', this.loadBookeroNews);
    this.$bus.$off('login-success', this.configureBugSnagUser);
    this.$bus.$off('login-success', this.configureGaUser);
    this.$bus.$off('update-favicon', this.updateFavicon);
    this.$bus.$off('force-update-waiting-queries', this.loadWaitingQueries);
    window.removeEventListener('resize', this.resizeContent);
  }
}
</script>
<style lang="scss">
@import '../../assets/vendor/css/normalize.css';
@import '../../assets/scss/variables.scss';
@import '../../assets/scss/mixins.scss';
@import '../../assets/scss/global.scss';
@import '../../assets/scss/overrides.scss';

body {
  overflow-x: hidden;

  &.no-scroll {
    overflow-y: hidden;
  }
}

.app {
  background: $color-bg-light;
  min-height: calc(100vh - 7rem);
  transition: all .25s ease-out;
  width: 100%;

  &-view {
    margin-left: 5.4rem;
    position: relative;
    width: calc(100% - 5.4rem);
  }

  &-content {
    display: flex;
    margin-top: 7rem;
    min-height: calc(100% - 7rem);
  }

  &.dark-bg {
    background: $color-primary;
  }
}

.switch-enter-to,
.switch-leave-to {
  transition: opacity .25s ease-out;
}

.switch-enter,
.switch-leave-to {
  left: 0;
  opacity: 0;
  position: absolute;
  top: 0;
  transform: none;
  width: 100%;
}

.app > a[href="#cookie-settings"] {
  color: #fff;
  bottom: 1.5rem;
  position: fixed;
  left: 50%;
  transform: translateX(-50%);

  &:before {
    content: "🍪 ";
  }

  &:active,
  &:focus,
  &:hover {
    opacity: .75;
  }
}

@media (max-width: 640px) {
  .app {
    min-height: calc(100vh - 5rem);

    &-view {
      margin-left: 0;
      width: 100%;
    }

    &-content {
      margin-top: 5rem;
      min-height: 100%;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/App.vue